// @ts-expect-error
import Bacon from 'baconjs';
import * as ramda from 'ramda';
import _ from 'underscore';
// @ts-expect-error
import include from 'underscore.string/include';

import {serializeFilters, serializeFields} from 'nutshell-core';

import {api} from 'nutshell-core/api';
import {Routing} from 'nutshell-core/routing';
import {getMultipleEntityType} from 'nutshell-core/utils/get-multiple-entity-type';

// @ts-expect-error
import {pushNotification, updateNotification} from '../../notifications';
// @ts-expect-error
import {refreshUser} from '../../user';

// @ts-expect-error
import queryFactory from './query';
import schemaFactory from './schema';

// @ts-expect-error
const $ = window.jQuery;

// Used for Map and Name queries, at present these are locked together
// to calculate the offset
const MAP_NAME_PAGE_SIZE = 500;
const DETAIL_PAGE_SIZE = 100;

/**
 * Calculate the page number based on the location's hash and page size
 * @param  {Object} location window.location
 * @param  {number} pageSize Number of rows in page
 * @return {number}          Page number
 */
export function calculatePage(location: Location, pageSize: number) {
    if (!pageSize || pageSize <= 0) {
        throw new RangeError('Page size must be greater than zero.');
    }
    const topRow = location.hash ? Number(location.hash.match(/\d+/)) | 0 : 0;

    return Math.floor(topRow / pageSize);
}

function mergeFailureNotificationMessage(body: string): string {
    const trimmed = body.trim();
    if (trimmed.includes('Target lead already merged')) {
        return 'Merge failed: Target lead already merged';
    }
    return 'Merge failed.';
}

// @ts-expect-error
function distinct(resetStream) {
    // @ts-expect-error
    let items = [];

    if (resetStream) {
        resetStream.onValue(() => {
            items = [];
        });
    }

    // @ts-expect-error
    return (item) => {
        // @ts-expect-error
        if (ramda.contains(item, items)) return false;
        items.push(item);

        return true;
    };
}

/**
 * Executes the requests for pages of the web filtering list based upon the
 * current query state.
 *
 * @param  {string} baseUrl - The absolute uri (without query parameters) for
 *                  the list being serviced.
 * @param  {Object} query - A query object pulled from a query stream.
 * @param  {Number} requestedPage - The page number being requested.
 * @param  {Object} options - Query options.
 * @param  {boolean} options.map - Is this a query for a map
 * @param  {boolean} options.reset - reset the page cache
 *
 * @return {Bacon.EventStream} - Single event stream wrapping a promise
 *                               from the ajax request
 */
export function executeSearchRequest(
    baseUrl: string,
    query: any,
    requestedPage: number,
    options: {
        map?: boolean;
        reset?: boolean;
        bare?: boolean;
    } = {}
) {
    let requestUrl = baseUrl;

    const checksum = query.checksum;
    const pageParam = {
        limit: DETAIL_PAGE_SIZE,
        page: requestedPage,
    } as const;

    if (options.map) {
        requestUrl = `${baseUrl}/map`;
        // @ts-expect-error
        pageParam.limit = MAP_NAME_PAGE_SIZE;
    }

    const fetchParams = {
        q: query.search.selected,
        filter: serializeFilters(query.filter.selected),
        fields: serializeFields(query.fields.selected),
        sort: query.sort.selected,
        page: pageParam,
    } as const;

    if (options.bare) {
        // @ts-expect-error
        fetchParams.page.limit = MAP_NAME_PAGE_SIZE;
        // @ts-expect-error
        fetchParams.bare = true;
    }

    return Bacon.fromPromise(
        api
            .get(requestUrl, fetchParams)
            // @ts-expect-error
            .then((res) => res.json())
            .then((result) => {
                result.checksum = checksum;
                result.offset =
                    requestedPage *
                    (options.map || options.bare ? MAP_NAME_PAGE_SIZE : DETAIL_PAGE_SIZE);

                return result;
            })
    );
}

/**
 * Merges the existing results with a new response from the server. If the
 * checksum has changed then we start a new array. We disregard any search
 * results who's checksum doesn't match the current query.
 *
 * @param {Object} currentItems - Immediately destructured
 * @param {string} currentItem.checksum - Checksum of query used to generate
 *                                        current results
 * @param {Object[]} currentItem.results - Ordered list of results for the query
 *                                         represnted by the qhecksum.
 *
 * @param {Object} searchResults - Immediately destructured
 * @param {number} searchResults.meta.count - The total number of items the
 * ]                                          current query can return
 *                                            (may exceed page size)
 * @param {Object[]} searchResults.listItems - Items in the query returned for
 *                                             the current page
 * @param {number} searchResults.offset - Offset of the results page from the
 *                                        beginning of the results arraw that
 *                                        for the listItems
 * @param {string} searchResults.checksum - Checksum of returned results,
 *                                          this can be compared with the
 *                                          existing checksum to tell us if
 *                                          we should clear the results.
 *
 * @param {Object} query - Object representation of current query user is
 *                       attempting to execute.
 * @param {string} query.checksum - Checksum to differentiate unique query states
 *
 * @return {Object} - An object containing the checksum of the most recnt
 *                    search page returned, and an array with the results from
 *                    the current filter set.
 */
// @ts-expect-error
function mergeResultSets(currentItems, searchResults, query) {
    const {checksum: listChecksum, results} = currentItems;
    const {
        meta: {count},
        listItems,
        offset,
        checksum: searchResultsChecksum,
    } = searchResults;
    if (searchResultsChecksum !== query.checksum) return currentItems;

    const currentResults = listChecksum === searchResultsChecksum ? results : new Array(count);

    const itemsOnPage = listItems.length;
    const resultSet = ramda
        .take(offset, currentResults)
        // @ts-expect-error
        .concat(listItems.map((item) => item.links.entity.id))
        .concat(ramda.takeLast(count - (offset + itemsOnPage), currentResults));

    return {checksum: searchResultsChecksum, results: resultSet};
}

/**
 * We need to ensure we're returning the latest fired searchResult's
 * facets, and to do that we need to leverage our checksum.
 * @param  {object} currentFacets currently held facets object from our
 * facets stream
 * @param  {object} searchResults latest detailResponseStream value, which
 * has a checksum property on it.
 * @param  {object} query         current query object, based on the url
 * @return {object}               latest facets object
 */
// @ts-expect-error
function getLatestFacets(currentFacets, searchResults, query) {
    if (searchResults.checksum !== query.checksum) {
        return currentFacets;
    }

    return searchResults.meta.facets;
}

/**
 * We need to ensure we're returning the latest fired searchResult's
 * stats (if a lead), and to do that we need to leverage our checksum.
 * @param  {object} currentStats currently held stats object from our
 * stats stream
 * @param  {object} searchResults latest detailResponseStream value, which
 * has a checksum property on it.
 * @param  {object} query         current query object, based on the url
 * @return {object}               latest stats object
 */
// @ts-expect-error
function getLatestLeadStats(currentStats, searchResults, query) {
    if (searchResults.checksum !== query.checksum) {
        return currentStats;
    }

    return searchResults.meta.stats;
}

/**
 * Handles toggling selection of the entire list.
 * @param {Object} currentSelected - Representation of the currently
 *                 selected items
 * @param {string} currentSelected.list - one of 'selected', 'omitted'
 * @param {Object[]} currentSelected.omitted - array of items omitted from
 *                   select all
 *
 * @return {Object} - Representation of currently selected items. Contains a
 *                  string indicating the list to use (selected or omitted). The
 *                  lists representing selected and omitted items, and a
 *                  function that will tell whether a given item is selected or
 *                  not
 */
// @ts-expect-error
function toggleList(currentSelected) {
    const {list, omitted} = currentSelected;
    if (list === 'omitted' && omitted.length === 0)
        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};

    return {list: 'omitted', selected: [], omitted: [], isSelected: () => true};
}

/**
 * Handles toggling selection of a specific item. This gets complicated;
 * because, when list selection is added in we need to consider that into the
 * formula.
 *
 * @param {Object} currentSelected - Representation of the currently
 *                 selected items
 * @param {string} currentSelected.list - one of 'selected', 'omitted'
 * @param {Object[]} currentSelected.selected - array of items manually
 *                   selected
 * @param {Object[]} currentSelected.omitted - array of items omitted from
 *                   select all
 * @param {String} toggledItemId - item that was toggled (either selected or
 *                 omitted)
 *
 * @return {Object} - Representation of currently selected items. Contains a
 *                  string indicating the list to use (selected or omitted). The
 *                  lists representing selected and omitted items, and a
 *                  function that will tell whether a given item is selected or
 *                  not
 */
export function toggleSelected(
    currentSelected: {
        list: 'selected' | 'omitted';
        selected: string[];
        omitted: string[];
    },
    toggledItemId: string
): {
    list: 'selected' | 'omitted';
    selected: string[];
    omitted: string[];
    isSelected: (arg1: string) => boolean;
} {
    const {[currentSelected.list]: list} = currentSelected;
    const updatedList: string[] = list.includes(toggledItemId)
        ? list.filter((id) => id !== toggledItemId)
        : list.concat(toggledItemId);

    // @ts-expect-error
    return ramda.mergeRight(currentSelected, {
        [currentSelected.list]: updatedList,
        isSelected: (id: string) => {
            // When we scroll, our items haven't fully loaded yet, we just have empty objects,
            // so we first need to check to make sure the links are there before we check to see
            // if the row is selected.
            return currentSelected.list === 'selected'
                ? updatedList.includes(id)
                : !updatedList.includes(id);
        },
    });
}

/**
 * Select the range between `mostRecentlyToggled[0]` and `toggledItem` and set them
 * to the new selected state of `toggledItem`
 *
 * @param {Object}   currentSelected - Representation of the currently
 *                   selected items
 * @param {string}   currentSelected.list - one of 'selected', 'omitted'
 * @param {string[]} currentSelected.selected - array of items manually
 *                   selected
 * @param {string[]} currentSelected.omitted - array of items omitted from
 *                   select all
 * @param {String}   toggledItemId - item that was toggled (either selected or
 *                   omitted)
 * @param {Object}   items - Representation of all items in the list
 * @param {Object}   mostRecentlyToggled - Representation of the item selected
 *                   just before toggledItem, if exists, in the shape:
 *                   [`mostRecentlyToggledItemId`, `toggledItemId`]
 *
 * @return {Object}  Representation of currently selected items. Contains a
 *                   string indicating the list to use (selected or omitted). The
 *                   lists representing selected and omitted items, and a
 *                   function that will tell whether a given item is selected or
 *                   not
 */
export function shiftToggleSelected(
    currentSelected: {
        list: 'selected' | 'omitted';
        selected: string[];
        omitted: string[];
    },
    toggledItemId: string,
    items: string[],
    mostRecentlyToggled: [string, string]
): {
    list: 'selected' | 'omitted';
    selected: string[];
    omitted: string[];
    isSelected: (arg1: string) => boolean;
} {
    // if we don't have a mostRecentlyToggled item, just bail and toggle the toggled item
    if (!mostRecentlyToggled[0]) return toggleSelected(currentSelected, toggledItemId);

    // currently selected list
    const {[currentSelected.list]: list} = currentSelected;

    // range of items we want to toggle
    const range = getRangeToToggle(list, toggledItemId, items, mostRecentlyToggled[0]);

    // @hack if we're trying to toggle a range greater than 100, just bail and toggle the toggled item
    // this is a hedge -- we should allow for selecting any size range,
    // and calculate the selected state as items load in on scroll
    if (range.length > 100) return toggleSelected(currentSelected, toggledItemId);

    const updatedList: string[] = !list.includes(toggledItemId)
        ? // @ts-expect-error
          [...new Set(list.concat(range))] // mostRecentlyToggled may have been duplicated
        : list.filter((id) => !range.includes(id));

    // @ts-expect-error
    return ramda.mergeRight(currentSelected, {
        [currentSelected.list]: updatedList,

        isSelected: (id: string) => {
            // When we scroll, our items haven't fully loaded yet, we just have empty objects,
            // so we first need to check to make sure the links are there before we check to see
            // if the row is selected.
            return currentSelected.list === 'selected'
                ? updatedList.includes(id)
                : !updatedList.includes(id);
        },
    });
}

/**
 * Calculate the range of items in `items` between `mostRecentlyToggledId` and `toggledItemId`
 *
 * @param {String[]} currentSelectedIds - Representation of the currently selected items
 * @param {String} toggledItemId - item that was toggled (either selected or
 *    omitted)
 * @param {Object} items - Representation of all items in the list
 * @param {String} mostRecentlyToggledId - Representation of the item selected
 *    just before toggledItem, if exists
 *
 * @return {Object} - Representation of the range of items in `items`
 *    between `mostRecentlyToggledId` and `toggledItemId`
 */
export function getRangeToToggle(
    currentSelectedIds: string[],
    toggledItemId: string,
    items: string[],
    mostRecentlyToggledId: string
): string[] {
    let rangeToToggle = items.filter((item) => {
        if (items.indexOf(toggledItemId) > items.indexOf(mostRecentlyToggledId)) {
            // toggledItemId is above mostRecentlyToggledId in the list:
            return (
                items.indexOf(item) < items.indexOf(toggledItemId) &&
                items.indexOf(item) > items.indexOf(mostRecentlyToggledId)
            );
        } else {
            // toggledItemId is below mostRecentlyToggledId in the list:
            return (
                items.indexOf(item) > items.indexOf(toggledItemId) &&
                items.indexOf(item) < items.indexOf(mostRecentlyToggledId)
            );
        }
    });

    // make sure mostRecentlyToggledId and toggledItemId are in rangeToToggle if they aren't already
    if (rangeToToggle && !rangeToToggle.includes(toggledItemId))
        rangeToToggle = rangeToToggle.concat(toggledItemId);
    if (currentSelectedIds && !rangeToToggle.includes(mostRecentlyToggledId))
        rangeToToggle = rangeToToggle.concat(mostRecentlyToggledId);

    return rangeToToggle.sort();
}

// @ts-expect-error
function getSelectedCount({total}, {list, selected, omitted}) {
    return {
        total,
        selected: list === 'selected' ? selected.length : total - omitted.length,
    };
}

/**
 * Helper method to serialize a footer action into analytics data
 * and send that to Analytics.
 *
 * @param {string} actionName - name of action take (export|merge|etc)
 * @param {integer} numSelected - number of entities selected in list
 * when action was clicked. A value of 0 indicates _selectAll_ was chosen
 *
 * @return {void}
 */
// @ts-expect-error
function trackFooterAction(actionName, numSelected) {
    const selectedValue = numSelected.length > 0 ? numSelected.length : 'all';
    const eventProps = {
        numSelected: selectedValue,
    } as const;

    // @ts-expect-error
    const eventAction = {
        export: Analytics.actions.filtering.didPerformExportAction,
        merge: Analytics.actions.filtering.didPerformMergeAction,
        delete: Analytics.actions.filtering.didPerformDeleteAction,
        email: Analytics.actions.filtering.didPerformEmailAction,
        mailchimp: Analytics.actions.filtering.didPerformMailchimpAction,
        addToAudience: Analytics.actions.filtering.didPerformAudienceAction,
        constantContact: Analytics.actions.filtering.didPerformConstantContactAction,
    }[actionName];

    Analytics.trackEvent(Analytics.categories.filtering, eventAction, eventProps);
}

/**
 * Factory method for the workhorse stream of entity list views. It provides a
 * property which contains all of the collected state for a particular entity
 * type.
 *
 * @param  {string} baseUrl - API url for the entity
 * @param  {Dispatcher} dispatcher - Dispatcher for use within this stream context
 * @param  {Object} options - Options object
 * @param  {string} options.topLevelEntity - One of accounts, contacts, leads
 * @param  {string[]} options.defaultFields - Default fields for entity type
 * @return {Bacon.Property} - Bacon.js Property with the collected state for a
 *                            particular entity type.
 */
export function listFactory(
    baseUrl: string,
    dispatcher: any,
    {
        topLevelEntity,
        defaultFields,
    }: {
        topLevelEntity: string;
        defaultFields: {
            accounts?: string[];
            contacts?: string[];
            leads?: string[];
        };
    }
) {
    const {end, plug, push, stream} = dispatcher;

    // @ts-expect-error
    function exportSelected(selectedItems, unused, query) {
        const {list, selected, omitted} = selectedItems;
        const data =
            list === 'selected' && selected.length > 0
                ? {
                      selectedIds: selected.join(','),
                      fields: serializeFields(query.fields.selected),
                  }
                : {
                      q: query.search.selected,
                      filter: serializeFilters(query.filter.selected),
                      fields: serializeFields(query.fields.selected),
                      omittedItems: omitted.join(','),
                  };

        // Track export action
        trackFooterAction('export', selected);

        api.get(`${baseUrl}/list`, data, {
            headers: new Headers({Accept: 'text/csv; charset=utf-8'}),
        })
            .then(() => {
                const userId = $('body').data('loggedInUser');
                refreshUser(userId);
            })
            .catch(() => pushNotification({type: 'error', message: 'Export failed.'}));

        return selectedItems;
    }

    // @ts-expect-error
    function mergeLeads({list, selected}, winner, query, results) {
        const processingNotificationId = pushNotification({
            type: 'processing',
            message: 'Processing…',
        });
        // Determine if we're deleting by *selection* or merging all items
        const selectedIds = list === 'selected' ? selected : results;

        // Figure out how many were deleted so we can display notification correctly.
        // In this case, "results" is the total number of items our list returned
        const numMerged = selected.length > 0 ? selected.length : results.length;

        // Track merge action
        trackFooterAction('merge', selected);

        // @ts-expect-error
        const leadsToMerge = selectedIds.filter((id) => id !== winner);

        api.post(`${baseUrl}/${winner}/merge`, {[topLevelEntity]: leadsToMerge})
            .then(() => push('refetchPage'))
            .then(() =>
                updateNotification(processingNotificationId, {
                    type: 'success',
                    message: `${numMerged} items merged.`,
                })
            )
            .catch((e) => {
                // @ts-expect-error
                e.httpResponse.text().then((body) => {
                    updateNotification(processingNotificationId, {
                        type: 'error',
                        message: mergeFailureNotificationMessage(body),
                    });
                });
            });

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    // @ts-expect-error
    function mergeSelected({list, selected}, unused, query, results) {
        // Default notification message while we're processing our request
        const processingNotificationId = pushNotification({
            type: 'processing',
            message: 'Processing…',
        });

        // Determine if we're deleting by *selection* or merging all items
        const selectedIds = list === 'selected' ? selected : results;

        // Figure out how many were deleted so we can display notification correctly.
        // In this case, "results" is the total number of items our list returned
        const numMerged = selected.length > 0 ? selected.length : results.length;

        // Track merge action
        trackFooterAction('merge', selected);

        const firstSelected = ramda.head(selectedIds);

        // Fire merge request
        if (firstSelected) {
            api.post(`${baseUrl}/${firstSelected}/merge`, {
                [topLevelEntity]: ramda.tail(selectedIds),
            })
                .then(() => push('refetchPage'))
                .then(() =>
                    updateNotification(processingNotificationId, {
                        type: 'success',
                        message: `${numMerged} items merged.`,
                    })
                )
                .catch((e) => {
                    // @ts-expect-error
                    e.httpResponse.text().then((body) => {
                        updateNotification(processingNotificationId, {
                            type: 'error',
                            message: mergeFailureNotificationMessage(body),
                        });
                    });
                });
        }

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    // @ts-expect-error
    function deleteSelected({list, selected, omitted}, unused, query, results) {
        // Default notification message while we're processing our request
        const processingNotificationId = pushNotification({
            type: 'processing',
            message: 'Processing…',
        });

        // Determine if we're deleting by *selection* or deleting all items
        const deletionParams =
            list === 'selected'
                ? {
                      selectedIds: selected.join(','),
                      csrfToken: Nut.getCsrfTokenFromDom(),
                  }
                : {
                      filter: serializeFilters(query.filter.selected),
                      q: query.search.selected,
                      omittedItems: omitted.join(','),
                      deleteAll: true,
                      csrfToken: Nut.getCsrfTokenFromDom(),
                  };

        // If a user omitted something but re-checked it, we want to remove
        // any notion of something being omitted
        if (list === 'omitted' && omitted.length < 1) {
            // @ts-expect-error
            delete deletionParams.omitted;
        }

        // Figure out how many were deleted so we can display notification correctly.
        // In this case, "results" is the total number of items our list returned
        const numDeleted = selected.length > 0 ? selected.length : results.length - omitted.length;

        // Track delete action
        trackFooterAction('delete', selected);

        // Fire delete request
        api.delete(decodeURI(`${baseUrl}?${Routing.param(deletionParams)}`))
            .then(() => {
                const userId = document.body && document.body.dataset.loggedInUser;
                refreshUser(userId);
                push('refetchPage');
            })
            .then(() =>
                updateNotification(processingNotificationId, {
                    type: 'success',
                    message: `${numDeleted} ${numDeleted !== 1 ? 'items' : 'item'} deleted.`,
                })
            )
            .catch(() =>
                updateNotification(processingNotificationId, {
                    type: 'error',
                    message: 'Delete failed.',
                })
            );

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    // @ts-expect-error
    function sendBulkEmail(selectedItems, {template, options}, query) {
        const {includeIncomplete, includeRecentlyReceivedTemplate} = options;

        const processingNotificationId = pushNotification({
            type: 'processing',
            message: 'Processing your email…',
        });
        const postData: Record<string, any> = {};
        postData.entityType = topLevelEntity;

        if (includeRecentlyReceivedTemplate) {
            postData.includeRecentRecipients = true;
        }

        // The user has checked the "Select all" button
        // In this case, we'll use solr, otherwise, we're just using the ids themselves.
        if (selectedItems.list === 'omitted') {
            // Need to clone here, or else it will have the side effect of
            // permanently adding this filter
            const filters =
                query.filter && query.filter.selected ? _.clone(query.filter.selected) : [];
            if (!includeIncomplete) filters.push({emailTemplate: template.id});

            if (Object.keys(filters).length > 0) {
                postData.filter = serializeFilters(filters);
            }
            if (query.search && query.search.selected) {
                postData.q = query.search.selected;
            }
            if (selectedItems.omitted.length > 0) {
                postData.omittedItems = selectedItems.omitted;
            }
        } else if (selectedItems && selectedItems.selected) {
            postData.includeIncomplete = includeIncomplete;
            postData.ids = selectedItems.selected;
        }

        // Track email action
        trackFooterAction('email', selectedItems.selected);

        if (!postData.ids || postData.ids.length > 0) {
            api.post(`emailtemplates/${template.id}/sends`, postData)
                .then(() =>
                    updateNotification(processingNotificationId, {
                        type: 'success',
                        message: 'Email sent.',
                    })
                )
                .catch(() =>
                    updateNotification(processingNotificationId, {
                        type: 'error',
                        message: 'Unable to send email.',
                    })
                );
        }

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    // @ts-expect-error
    function exportToMailChimp(selectedItems, unused, query) {
        const {selected, list: listName} = selectedItems;
        const defaultParams = {
            newFilters: true,
            target: 'MailChimp',
            listId: $('#mailchimp-list-id').val(),
            sendWelcome: $('#mailchimp-send-welcome').is(':checked') ? 1 : 0,
        } as const;

        const postData =
            listName === 'selected'
                ? {
                      ...defaultParams,
                      selectedIds: selected,
                  }
                : {
                      ...defaultParams,
                      filter: serializeFilters(query.filter.selected),
                      q: query.search.selected,
                  };
        if (listName === 'omitted') {
            if (selectedItems.omitted.length > 0) {
                // @ts-expect-error
                postData.omittedItems = selectedItems.omitted;
            }
        }

        // Track mailchimp action
        trackFooterAction('mailchimp', selected);

        Nut.ajax.post({
            url: `/${getMultipleEntityType(topLevelEntity)}/export`,
            data: postData,
            success: function () {
                Popover.close();
                Popmini.open('Exporting contacts to MailChimp. They should be visible shortly.', {
                    attachTo: $('.popover-opener-mailchimp'),
                    classes: 'type-message',
                    orientation: 'north',
                });
            },
        });

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    // @ts-expect-error
    function exportToConstantContact(selectedItems) {
        const {selected} = selectedItems;

        // Track constant contact action
        trackFooterAction('constantContact', selected);

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    // @ts-expect-error
    function addToAudience(selectedItems) {
        const {selected} = selectedItems;

        // Track audience action
        trackFooterAction('addToAudience', selected);

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    function bulkEditComplete() {
        push('refetchPage');

        return {list: 'selected', selected: [], omitted: [], isSelected: () => false};
    }

    const schema = schemaFactory(`${baseUrl}/list`, dispatcher, topLevelEntity);
    const query = queryFactory(dispatcher, {topLevelEntity, defaultFields});

    // @ts-expect-error
    function isValidQuery(q) {
        const {fields, queryString} = q;

        return (
            fields.selected[topLevelEntity].length > 0 &&
            !include(decodeURIComponent(queryString), `fields[${topLevelEntity}]=&`)
        );
    }

    const isScrolling = stream('startScrolling').awaiting(stream('stopScrolling')).startWith(false);

    /*
     * Stream to determine when the query has changed in a manner that we should
     * go and fetch from the server.
     */
    const querySetChanged = query
        .changes()
        .filter(isValidQuery)
        // @ts-expect-error
        .filter(({view: {selected}}) => selected !== 'map')
        // @ts-expect-error
        .map(({checksum}) => checksum)
        .skipDuplicates();

    /*
     * Requests for the initial row for a particularl query set
     * initially this is the value from the hash or 0. And then become 0
     * each time the query set changes.
     */
    const indexIntializationRequests = stream('initialize')
        // @ts-expect-error
        .map((options = {}) => options.startRow || 0)
        .concat(querySetChanged.map(0));

    /*
     * We care about page requests for details and names in the following situations.
     * 1. The first time a page is requested after the querySet has changed
     * 2. After the size of the list changes a second time
     * 3. When a page is specifically requested
     */
    const detailPageRequests = querySetChanged
        .flatMapLatest(() => {
            return (
                stream('requestedIndex')
                    .merge(indexIntializationRequests)
                    // @ts-expect-error
                    .map((index) => Math.floor(index / DETAIL_PAGE_SIZE))
                    .holdWhen(isScrolling) // While they are scrolling hold the page request
                    .skipDuplicates() // Only care when the page changes
                    .slidingWindow(2) // Grab the last two pages potentially, in case they are on a page boundary
                    .flatMapLatest(Bacon.fromArray) // Now change that window array back into separate page requests
                    .filter(
                        distinct(
                            stream('LIST-ITEMS').map('.results.length').skipDuplicates().skip(1)
                        )
                    )
                    .skipDuplicates() // Seems to prevent occasional infinate ajax loop
                    // @ts-expect-error
                    .map((page) => ({page}))
            );
        })
        .merge(
            stream('refetchPage').map(() => {
                const page = calculatePage(window.location, DETAIL_PAGE_SIZE);

                return {page};
            })
        );

    const namePageRequests = querySetChanged.flatMapLatest(() => {
        return (
            stream('requestedIndex')
                .merge(indexIntializationRequests)
                // @ts-expect-error
                .map((index) => Math.floor(index / MAP_NAME_PAGE_SIZE))
                /* Since this is just an optimization let's just be sure they are done
                 * before we fire off a request
                 */
                .debounce(200)
                .filter(
                    distinct(stream('LIST-ITEMS').map('.results.length').skipDuplicates().skip(1))
                )
                .skipDuplicates() // Seems to prevent occasional infinate ajax loop
                // @ts-expect-error
                .map((page) => ({page}))
        );
    });

    /*
     * We create a map request whenever the query changes, and it is a map
     * query.
     */
    const mapRequestStream = query
        .changes()
        .debounce(200)
        // @ts-expect-error
        .filter(({view: {selected}}) => selected === 'map');

    /*
     * Streams representing the the responses from the ajax requests for each
     * request tupe
     */
    const detailResponseStream = query
        // @ts-expect-error
        .sampledBy(detailPageRequests, (q, pageAndOptions) => ({query: q, ...pageAndOptions}))
        // @ts-expect-error
        .flatMap(({query: q, page, options}) =>
            executeSearchRequest(`${baseUrl}/list`, q, page, options)
        )
        .holdWhen(isScrolling)
        .doError(() => push('searchError'));

    // We deliberately eat the errors on the name request stream. As long as
    // the detail stream is working we are fine, and we don't want to show the
    // error screen when the name query fails.
    const nameResponseStream = query
        // @ts-expect-error
        .sampledBy(namePageRequests, (q, pageAndOptions) => ({query: q, ...pageAndOptions}))
        // @ts-expect-error
        .flatMap(({query: q, page}) =>
            executeSearchRequest(`${baseUrl}/list`, q, page, {bare: true})
        )
        .holdWhen(isScrolling);

    const mapResponseStream = mapRequestStream
        // @ts-expect-error
        .flatMapLatest((q) => executeSearchRequest(`${baseUrl}/list`, q, 0, {map: true}))
        .doError(() => push('searchError'));

    /*
     * Final response handling for all 3 response streams. They are held while
     * we are scrolling. This is because fixed-data-table is rerendering
     * while it is scrolling, and we can't update the model while that is
     * happening. By doing a hold like this we allow the actual ajax requests
     * to roundtrip from the server.
     */
    const searchResults = detailResponseStream
        .merge(mapResponseStream)
        .merge(nameResponseStream)
        .doAction(() => push('clearError'))
        // @ts-expect-error
        .doError((error) => {
            Nut.error(
                'Entity List Search Error',
                JSON.stringify({
                    ...error,
                    userId: $('body').data('loggedInUser'),
                    location: window.location.href,
                })
            );
        });

    const items = Bacon.update(
        {checksum: undefined, results: []},
        [searchResults, query],
        mergeResultSets
    );

    const facets = Bacon.update({}, [searchResults, query], getLatestFacets);

    // for the streams in this const, we want to update the list item that
    // was most recently interacted with
    const mostRecentlyToggledItem = Bacon.update(
        [null, null],
        [stream('toggleItem')],
        updateMostRecentlyToggledItem,
        [stream('shiftSelectItem')],
        updateMostRecentlyToggledItem,
        // when we change filters, we don't want old recently selected items
        // hanging around, so we bust 'em
        [querySetChanged],
        clearMostRecentlyToggledItem
    );

    // even though all this really needs to provide is `currentSelected`,
    // this was the only way to stash this data in state,
    // so the toggled item comes along for the ride
    // @ts-expect-error
    function updateMostRecentlyToggledItem(currentSelected, toggledItemId) {
        return [currentSelected[1], toggledItemId];
    }

    function clearMostRecentlyToggledItem() {
        return [null, null];
    }

    const leadStats = Bacon.update({}, [searchResults, query], getLatestLeadStats);

    plug('LIST-ITEMS', items.changes());

    // This is the heavy lifting function. It converts each item from the
    // incoming search results, and converts it into an entry in a map. It will
    // merge that item with any existing data.
    // @ts-expect-error
    const entityDataStream = searchResults.scan({[undefined]: {}}, (entityData, results) => {
        // @ts-expect-error
        return results.listItems.reduce((data, item) => {
            const id = item.links.entity.id;

            return {
                ...data, // Start with all exiting items
                [id]: {
                    // Update the item we are currently reading from results
                    ...data[id], // Use all existing top level data
                    ...item, // Overwrite it with new data from the results
                    id, // Use the entity id, instead of hashid
                    fields: {
                        // Be certain we copied all of the fields
                        ...(data[id] || {}).fields, // Grab existing field data first
                        ...item.fields, // Overwrite any matching keys with data from results
                    },
                },
            };
        }, entityData);
    });

    stream('watchForNew')
        .flatMapLatest(() =>
            Bacon.repeatedly(1000, 'Fetch Page').takeUntil(
                Bacon.later(10000, '10 Seconds Elapsed').merge(
                    items.map('.results.length').skipDuplicates().changes().map('Count Changed')
                )
            )
        )
        .onValue(() => push('refetchPage'));

    const selectedItems = Bacon.update(
        {list: 'selected', selected: [], omitted: [], isSelected: () => false},
        [querySetChanged],
        () => ({list: 'selected', selected: [], omitted: [], isSelected: () => false}),
        [stream('selectAll')],
        toggleList,
        [stream('toggleItem')],
        toggleSelected,
        [stream('shiftSelectItem'), items.map('.results'), mostRecentlyToggledItem],
        shiftToggleSelected,

        [stream('mergeSelectedItems'), query, items.map('.results')],
        mergeSelected,
        [stream('mergeLeads'), query, items.map('.results')],
        mergeLeads,
        [stream('editComplete')],
        bulkEditComplete,
        [stream('deleteSelectedItems'), query, items.map('.results')],
        deleteSelected,
        [stream('exportSelectedItems'), query],
        exportSelected,
        [stream('exportSelectedItemsToMailChimp'), query],
        exportToMailChimp,
        [stream('exportSelectedItemsToConstantContact'), query],
        exportToConstantContact,
        [stream('addSelectedItemsToAudience'), query],
        addToAudience,
        [stream('sendEmail'), query],
        sendBulkEmail
    );

    const selectedCount = Bacon.update(
        {total: 0, selected: 0},
        [querySetChanged],
        () => ({total: 0, selected: 0}),
        // @ts-expect-error
        [items.changes().map(({results}) => results.length)],
        // @ts-expect-error
        ({selected}, resultCount) => ({selected, total: resultCount}),
        [selectedItems.changes(), items.map('.results')],
        getSelectedCount
    );

    /**
     * Row getter for the UI. This also will initiate ajax requests for
     * the page containing the requested row.
     */
    const getRow = Bacon.update(
        () => {},
        [items.changes(), entityDataStream],
        // @ts-expect-error
        (unused, {results}, entityData) => {
            // @ts-expect-error
            return _.memoize((idx) => {
                push('requestedIndex', idx);

                return entityData[results[idx]];
            });
        }
    );

    return Bacon.combineTemplate({
        initialize() {
            // When we initialize the list we check to see if there is a row hash to begin on
            // This is important as it keys us to load the correct pages. For more info
            // on how this works see:
            // http://stackoverflow.com/questions/7425502/how-to-return-integer-from-url-hash-using-javascript/7425565#7425565
            // @ts-expect-error
            const startRow = window.location.hash.match(/\d+/) | 0;
            push('initialize', {startRow});
            end('initialize');
        },
        firstLoad: Bacon.update(true, [items.changes()], () => false),
        startScrolling() {
            push('startScrolling');
        },
        stopScrolling() {
            push('stopScrolling');
        },

        schema,

        query,
        leadStats,
        facets,
        filtering: querySetChanged.awaiting(searchResults),
        loading: query.changes().skipDuplicates(_.isEqual).awaiting(detailResponseStream),
        entityData: entityDataStream,
        // @ts-expect-error
        items: items.map(({results}) => results),
        count: items.map('.results.length').startWith(0),
        countFormatted: searchResults.map('.meta.countFormatted').startWith(0),
        unfilteredCount: searchResults.map('.meta.total').startWith(0),
        unfilteredCountFormatted: searchResults.map('.meta.totalFormatted').startWith(0),
        getRow,

        hasError: stream('searchError').awaiting(stream('clearError')),
        clearError: () => push('clearError'),

        allSelected: selectedItems.map(
            // @ts-expect-error
            ({list, omitted}) => list === 'omitted' && omitted.length === 0
        ),
        // @ts-expect-error
        massSelect: selectedItems.map(({list}) => list === 'omitted'),
        // @ts-expect-error
        isSelected: selectedItems.map(({isSelected}) => {
            return isSelected;
        }),
        // @ts-expect-error
        selectedItems: selectedItems.map(({selected}) => selected),
        // @ts-expect-error
        omittedItems: selectedItems.map(({omitted}) => omitted),
        // @ts-expect-error
        selectedCount: selectedCount.map(({selected}) => selected),
        // @ts-expect-error
        omittedCount: selectedItems.map(({omitted}) => omitted.length),

        selectAllItems() {
            push('selectAll');
        },
        // @ts-expect-error
        toggleItem(id) {
            push('toggleItem', id);
        },
        // @ts-expect-error
        shiftSelectItem(id) {
            push('shiftSelectItem', id);
        },
        clearSelectedItems() {
            push('clearSelectedItems');
        },

        editComplete() {
            push('editComplete');
        },
        exportSelectedItems() {
            push('exportSelectedItems');
        },
        exportSelectedItemsToMailChimp() {
            push('exportSelectedItemsToMailChimp');
        },
        // @ts-expect-error
        exportSelectedItemsToConstantContact(data) {
            push('exportSelectedItemsToConstantContact', data);
        },
        // @ts-expect-error
        addSelectedItemsToAudience(data) {
            push('addSelectedItemsToAudience', data);
        },
        mergeSelectedItems() {
            push('mergeSelectedItems');
        },
        // @ts-expect-error
        mergeLeads(winner) {
            push('mergeLeads', winner);
        },
        // @ts-expect-error
        sendEmail(template, options) {
            push('sendEmail', {template, options});
        },
        deleteSelectedItems() {
            push('deleteSelectedItems');
        },
        fetchMapData() {
            push('fetchMapData', 0);
        },
        watchForNew() {
            push('watchForNew');
        },
        refetchPage() {
            push('refetchPage');
        },
    });
}
